/**
 * Wallet signing + POST /exchange. Every order/cancel is signed by the user's EIP-1193 wallet (MetaMask etc).
 *
 * MetaMask only signs EIP-712 data whose domain chainId equals the wallet's active chain, and L1 actions use
 * chainId 1337, so (as in docs/spec/website_example) we switch the wallet to 0x539 first, adding a placeholder
 * "Hyperliquid EIP712 Signing" network if the wallet does not know it. See docs/research/S4_TESTNET.md for UX notes.
 * Other wallets differ (Frame signs without switching, Rabby probes the RPC, Phantom refuses custom chains): the
 * per-wallet handling lives in signWithChain() + wallet/compat.ts, researched in docs/research/WALLETS.md (#687).
 */
import type { Hex, TypedDataDefinition } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import type { EnvConfig } from '../config/env';
import { reportExchangeTransport } from './healthMonitor';
import { walletProfile } from '../wallet/compat';
import {
  isChainMismatch,
  isRequestPending,
  isUnknownChain,
  isUserRejection,
  walletErrorCodes,
  walletErrorMessage,
} from '../wallet/errors';
import { withChainSwitch } from '../wallet/switchGuard';
import type { EIP1193Provider } from '../wallet/types';
import {
  actionHash,
  APPROVE_AGENT_PRIMARY_TYPE,
  APPROVE_AGENT_TYPES,
  APPROVE_BUILDER_FEE_PRIMARY_TYPE,
  APPROVE_BUILDER_FEE_TYPES,
  approveBuilderFeeAction,
  l1TypedData,
  splitSig,
  userSignedTypedData,
  type Signature,
  type TypedDataPayload,
} from './signing';

export interface Eip1193Provider {
  request(args: { method: string; params?: unknown[] | Record<string, unknown> }): Promise<unknown>;
}

export const L1_CHAIN_ID_HEX = '0x539' as const; // 1337

/**
 * Placeholder network for L1 signing. There is no real chain 1337: nothing is ever sent to the RPC by us, but some
 * wallets probe it when the network is added (Rabby refuses to save it unless eth_chainId answers 0x539; see
 * docs/research/WALLETS.md). VITE_L1_SIGNING_RPC_URL can point at a stub that answers, once one exists.
 * blockExplorerUrls is included because Rainbow requires a non-empty list; MetaMask accepts any https URL.
 */
export const L1_SIGNING_RPC_URL: string =
  (import.meta.env?.VITE_L1_SIGNING_RPC_URL as string | undefined) || 'http://127.0.0.1:8545';

export const L1_CHAIN_PARAMS = {
  chainId: L1_CHAIN_ID_HEX,
  chainName: 'Hyperliquid EIP712 Signing',
  rpcUrls: [L1_SIGNING_RPC_URL],
  nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
  blockExplorerUrls: ['https://app.hyperliquid.xyz/explorer'],
} as const;

export const ARBITRUM_SEPOLIA_PARAMS = {
  chainId: '0x66eee',
  chainName: 'Arbitrum Sepolia',
  rpcUrls: ['https://sepolia-rollup.arbitrum.io/rpc'],
  nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
  blockExplorerUrls: ['https://sepolia.arbiscan.io'],
} as const;

export interface ChainOptions {
  /** EIP-6963 rdns of the wallet ('injected' or null for legacy window.ethereum); selects the per-wallet profile. */
  walletRdns?: string | null;
  /**
   * Switch back to the chain the wallet was on before signing. Default false: it costs a second network prompt per
   * order in MetaMask / Coinbase and nothing in the app depends on the active chain (docs/research/WALLETS.md).
   */
  restoreChain?: boolean;
  /** Override the post-switch settle poll (tests). */
  settle?: { tries: number; delayMs: number };
}

/**
 * The wallet could not sign an L1 action: it neither switches to chain 1337 nor signs typed data for another chain.
 * `cause` is the underlying wallet error.
 */
export class WalletChainUnsupportedError extends Error {
  readonly code = 'HL_CHAIN_UNSUPPORTED';
  constructor(
    readonly walletName: string,
    readonly cause?: unknown,
    hint?: string,
  ) {
    super(
      hint ??
        `${walletName} can't sign Hyperliquid orders: it refused the "Hyperliquid EIP712 Signing" network (chain 1337)` +
          (cause ? ` (${walletErrorMessage(cause)})` : '') +
          '. Enable 1-click trading (one approval on your current network), or try MetaMask.',
    );
    this.name = 'WalletChainUnsupportedError';
  }
}

/**
 * wallet_addEthereumChain failed (rejected by the user, or refused by the wallet). Keeps the original error reachable
 * through data.originalError, so isUserRejection() etc. still see its code.
 */
export class ChainAddError extends Error {
  readonly code: number | undefined;
  readonly data: { originalError: unknown };
  constructor(readonly original: unknown) {
    super(walletErrorMessage(original));
    this.name = 'ChainAddError';
    this.code = walletErrorCodes(original)[0];
    this.data = { originalError: original };
  }
}

const sameChain = (a: unknown, b: string) => typeof a === 'string' && parseInt(a, 16) === parseInt(b, 16);

async function readChain(provider: Eip1193Provider): Promise<string | null> {
  try {
    const c = await provider.request({ method: 'eth_chainId' });
    if (typeof c === 'string') return c;
    if (typeof c === 'number') return `0x${c.toString(16)}`;
    return null;
  } catch {
    return null;
  }
}

/**
 * wallet_switchEthereumChain → on "unknown chain" (4902, -32603-wrapped 4902, or the text) wallet_addEthereumChain
 * → switch again (a no-op for wallets that already switched as part of the add). User rejections and other errors
 * propagate. Holds the switch guard so WalletProvider ignores the events wallets emit while switching.
 */
async function switchOrAdd(provider: Eip1193Provider, params: { chainId: string } & Record<string, unknown>): Promise<void> {
  await withChainSwitch(async () => {
    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: params.chainId }] });
    } catch (e) {
      if (isUserRejection(e) || !isUnknownChain(e)) throw e;
      try {
        await provider.request({ method: 'wallet_addEthereumChain', params: [params] });
      } catch (addErr) {
        throw new ChainAddError(addErr);
      }
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: params.chainId }] });
    }
  });
}

/** Switch the wallet to `params.chainId`, adding the chain on "unknown chain" (4902 in any of its forms). */
export async function ensureChain(
  provider: Eip1193Provider,
  params: { chainId: string } & Record<string, unknown>,
): Promise<void> {
  const current = await provider.request({ method: 'eth_chainId' });
  if (sameChain(current, params.chainId)) return;
  await switchOrAdd(provider, params);
}

/** Wait (briefly) until the wallet reports `chainId`; some wallets resolve the switch before they apply it. */
async function settleOn(provider: Eip1193Provider, chainId: string, s = { tries: 10, delayMs: 100 }): Promise<boolean> {
  for (let i = 0; i < s.tries; i++) {
    if (sameChain(await readChain(provider), chainId)) return true;
    await new Promise((r) => setTimeout(r, s.delayMs));
  }
  return sameChain(await readChain(provider), chainId);
}

/** Errors after which a sign-first wallet gets the switch-to-1337 treatment instead of a hard failure. */
function retryableSignError(e: unknown): boolean {
  if (isUserRejection(e) || isRequestPending(e)) return false;
  if (isChainMismatch(e)) return true;
  const c = walletErrorCodes(e);
  return c.includes(-32603) || c.includes(-32602) || c.includes(-32000) || c.length === 0;
}

/**
 * Sign typed data whose domain chainId is `params.chainId`, handling the per-wallet chain rules (#687):
 *
 *   switch-first (MetaMask, Coinbase, OKX, Brave, …, unknown wallets):
 *     switch/add → sign. If the switch fails with anything but a user rejection (wallet refuses custom chains:
 *     -32602 / 4200 / -32603), still try to sign on the current chain: wallets that do not enforce the chainId
 *     match sign fine. If the wallet then refuses → WalletChainUnsupportedError.
 *     If the switch "succeeded" but signing reports a mismatch, wait for the chain to settle and retry once.
 *   sign-first (Rabby):
 *     sign on the current chain; on a chain-mismatch / internal error fall back to switch/add → sign.
 *
 * A user rejection (4001, nested or text) always propagates unchanged.
 */
export async function signWithChain<T>(
  provider: Eip1193Provider,
  params: { chainId: string } & Record<string, unknown>,
  sign: () => Promise<T>,
  opts: ChainOptions = {},
): Promise<T> {
  const profile = walletProfile(opts.walletRdns ?? null, provider as unknown as EIP1193Provider);
  const original = await provider.request({ method: 'eth_chainId' });
  const onTarget = sameChain(original, params.chainId);
  let switched = false;
  let switchError: unknown = null;

  const restore = async () => {
    if (!opts.restoreChain || !switched || typeof original !== 'string') return;
    try {
      await withChainSwitch(() => provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: original }] }));
    } catch {
      /* best effort: the user can switch back in the wallet */
    }
  };

  try {
    if (!onTarget && profile.strategy === 'sign-first') {
      try {
        return await sign();
      } catch (e) {
        if (!retryableSignError(e)) throw e;
        await switchOrAdd(provider, params);
        switched = true;
        return await sign();
      }
    }

    if (!onTarget) {
      try {
        await switchOrAdd(provider, params);
        switched = true;
      } catch (e) {
        if (isUserRejection(e) || isRequestPending(e)) throw e;
        switchError = e; // wallet refuses custom chains: maybe it signs anyway
      }
    }

    try {
      return await sign();
    } catch (e) {
      if (isUserRejection(e)) throw e;
      const hint = switchError instanceof ChainAddError ? profile.addChainHint : undefined;
      if (switchError) throw new WalletChainUnsupportedError(profile.name, switchError, hint);
      if (!isChainMismatch(e)) throw e;
      // Switched (or already on the chain) but the wallet still sees another one: let it settle, retry once.
      if (await settleOn(provider, params.chainId, opts.settle)) return await sign();
      throw new WalletChainUnsupportedError(profile.name, e);
    }
  } finally {
    await restore();
  }
}

export interface ExchangePayload {
  action: unknown;
  nonce: number;
  signature: Signature;
  vaultAddress: null;
}

/** eth_signTypedData_v4 with the JSON-encoded payload; returns {r,s,v}. */
export async function signTypedDataV4(
  provider: Eip1193Provider,
  account: Hex,
  typedData: TypedDataPayload,
): Promise<Signature> {
  const sig = await provider.request({ method: 'eth_signTypedData_v4', params: [account, JSON.stringify(typedData)] });
  if (typeof sig !== 'string') throw new Error('wallet returned a non-string signature');
  return splitSig(sig);
}

/**
 * Sign an L1 action (order, cancel, ...) with the user's wallet. Switches the wallet to chain 1337 first.
 * `nonce` should be Date.now() taken right before signing (HL rejects nonces far from server time).
 */
export async function signL1(
  provider: Eip1193Provider,
  account: Hex,
  action: unknown,
  nonce: number | (() => number),
  env: Pick<EnvConfig, 'signingSource'>,
  opts: ChainOptions = {},
): Promise<ExchangePayload> {
  // A nonce function is called once, right before the first signature request (after any chain switch for
  // switch-first wallets). A sign-first retry re-signs the same nonce + hash.
  let n: number | null = typeof nonce === 'number' ? nonce : null;
  const signature = await signWithChain(
    provider,
    L1_CHAIN_PARAMS,
    () => {
      if (n === null) n = (nonce as () => number)();
      return signTypedDataV4(provider, account, l1TypedData(actionHash(action, null, n, null), env.signingSource));
    },
    opts,
  );
  return { action, nonce: n as number, signature, vaultAddress: null };
}

/** Sign approveAgent (user-signed, chainId 0x66eee). Mutates nothing the caller passed in. */
export async function signApproveAgent(
  provider: Eip1193Provider,
  account: Hex,
  agentAddress: Hex,
  agentName: string,
  nonce: number,
  env: Pick<EnvConfig, 'isMainnet'>,
  opts: ChainOptions = {},
): Promise<ExchangePayload> {
  const action: Record<string, unknown> = { type: 'approveAgent', agentAddress, agentName, nonce };
  const td = userSignedTypedData(APPROVE_AGENT_PRIMARY_TYPE, APPROVE_AGENT_TYPES, action, env.isMainnet);
  const signature = await signWithChain(provider, ARBITRUM_SEPOLIA_PARAMS, () => signTypedDataV4(provider, account, td), opts);
  return { action, nonce, signature, vaultAddress: null };
}

/**
 * The wallet could not tell us its current chain (eth_chainId failed or returned junk), so approveAgent cannot be
 * signed on it.
 */
export class WalletChainUnknownError extends Error {
  readonly code = 'HL_CHAIN_UNKNOWN';
  constructor(what = '1-click trading cannot be enabled') {
    super(`The wallet did not report its current network, so ${what}.`);
    this.name = 'WalletChainUnknownError';
  }
}

/**
 * Sign approveAgent (#688) on the wallet's CURRENT chain: signatureChainId = domain chainId = eth_chainId. Never
 * switches networks, so wallets that cannot add chain 1337 (Phantom, most mobile wallets) can enable agent trading.
 * Hyperliquid accepts any signatureChainId for user-signed actions (docs/research/AGENT_WALLET.md).
 */
export async function signApproveAgentOnCurrentChain(
  provider: Eip1193Provider,
  account: Hex,
  agentAddress: Hex,
  agentName: string,
  nonce: number,
  env: Pick<EnvConfig, 'isMainnet'>,
): Promise<{ payload: ExchangePayload; signatureChainId: Hex }> {
  const raw = await readChain(provider);
  const id = raw === null ? NaN : parseInt(raw, 16);
  if (!Number.isSafeInteger(id) || id <= 0) throw new WalletChainUnknownError();
  const chainHex = `0x${id.toString(16)}` as Hex;
  const action: Record<string, unknown> = { type: 'approveAgent', agentAddress, agentName, nonce };
  const td = userSignedTypedData(APPROVE_AGENT_PRIMARY_TYPE, APPROVE_AGENT_TYPES, action, env.isMainnet, chainHex);
  const signature = await signTypedDataV4(provider, account, td);
  return { payload: { action, nonce, signature, vaultAddress: null }, signatureChainId: chainHex };
}

/**
 * Sign approveBuilderFee (#770) on the wallet's CURRENT chain, exactly like signApproveAgentOnCurrentChain (no network
 * switch; Hyperliquid accepts any signatureChainId for user-signed actions). Must be signed by the user's own wallet:
 * an agent key cannot approve builder fees. `maxFeeRate` is a percent string ("0.01%").
 */
export async function signApproveBuilderFeeOnCurrentChain(
  provider: Eip1193Provider,
  account: Hex,
  builder: Hex | string,
  maxFeeRate: string,
  nonce: number,
  env: Pick<EnvConfig, 'isMainnet'>,
): Promise<{ payload: ExchangePayload; signatureChainId: Hex }> {
  const raw = await readChain(provider);
  const id = raw === null ? NaN : parseInt(raw, 16);
  if (!Number.isSafeInteger(id) || id <= 0) throw new WalletChainUnknownError('the fee approval cannot be signed');
  const chainHex = `0x${id.toString(16)}` as Hex;
  const action = approveBuilderFeeAction(maxFeeRate, builder, nonce);
  const td = userSignedTypedData(APPROVE_BUILDER_FEE_PRIMARY_TYPE, APPROVE_BUILDER_FEE_TYPES, action, env.isMainnet, chainHex);
  const signature = await signTypedDataV4(provider, account, td);
  return { payload: { action, nonce, signature, vaultAddress: null }, signatureChainId: chainHex };
}

/** viem derives the EIP712Domain type from `domain` itself. */
function forViem(td: TypedDataPayload): TypedDataDefinition {
  const { EIP712Domain: _omit, ...types } = td.types;
  void _omit;
  return { domain: td.domain, types, primaryType: td.primaryType, message: td.message } as unknown as TypedDataDefinition;
}

/**
 * Sign an L1 action (order, cancel) locally with an approved agent key (#688): the same msgpack → actionHash →
 * phantom-agent EIP-712 path as signL1, but the signer is the agent, so there is no wallet prompt and no chain
 * switch. Hyperliquid maps the recovered agent address to the user that approved it.
 */
export async function signL1WithAgent(
  agentKey: Hex,
  action: unknown,
  nonce: number,
  env: Pick<EnvConfig, 'signingSource'>,
): Promise<ExchangePayload> {
  const td = l1TypedData(actionHash(action, null, nonce, null), env.signingSource);
  const sig = await privateKeyToAccount(agentKey).signTypedData(forViem(td));
  return { action, nonce, signature: splitSig(sig), vaultAddress: null };
}

export type ExchangeResponse =
  | { status: 'ok'; response: unknown }
  | { status: 'err'; response: string }
  | { status: string; response: unknown };

/** POST the signed payload to env.exchangeUrl. Throws on HTTP errors with the body text. */
export async function postExchange(
  env: Pick<EnvConfig, 'exchangeUrl'>,
  payload: ExchangePayload,
  fetchImpl: typeof fetch = fetch,
): Promise<ExchangeResponse> {
  let res: Response;
  try {
    res = await fetchImpl(env.exchangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    // Transport failure (not an order rejection): feeds the outage notice (#771).
    reportExchangeTransport(env.exchangeUrl, false);
    throw e;
  }
  reportExchangeTransport(env.exchangeUrl, res.status < 500);
  const text = await res.text();
  if (!res.ok) throw new Error(`HTTP ${res.status} from /exchange: ${text}`);
  try {
    return JSON.parse(text) as ExchangeResponse;
  } catch {
    throw new Error(`non-JSON /exchange response: ${text}`);
  }
}
